import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { facilityApi } from '../../api/api';
import { useAuth } from '../../context/AuthContext';
import { notify } from '../../utils/notifications';
import { roleLabel } from '../../utils/facilityRoles';
import RoleGuard from './RoleGuard';
import TeamMemberRow from './TeamMemberRow';
import TeamInviteRow from './TeamInviteRow';
import InviteComposer from './InviteComposer';
import '../../styles/FacilityTeam.css';

export default function FacilityTeam() {
    const { facilityId } = useParams();
    return (
        <RoleGuard requireFacilityAdmin facilityId={facilityId}>
            <FacilityTeamInner facilityId={facilityId} />
        </RoleGuard>
    );
}

function FacilityTeamInner({ facilityId }) {
    const { user } = useAuth();
    const [facility, setFacility] = useState(null);
    const [members, setMembers] = useState([]);
    const [invites, setInvites] = useState([]);
    const [loading, setLoading] = useState(true);
    const [composerOpen, setComposerOpen] = useState(false);

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const [fac, mem, inv] = await Promise.all([
                facilityApi.get(facilityId),
                facilityApi.listMembers(facilityId),
                facilityApi.listInvites(facilityId),
            ]);
            setFacility(fac);
            setMembers(mem);
            setInvites(inv);
        } catch (e) {
            notify.error('Failed to load team data');
        } finally {
            setLoading(false);
        }
    }, [facilityId]);

    useEffect(() => { reload(); }, [reload]);

    const onChangeRole = async (uid, newRole) => {
        const isSelf = user?.uid === uid;
        if (isSelf && newRole !== 'facility_admin') {
            if (!window.confirm("Are you sure? You'll lose access to manage this facility.")) {
                return;
            }
        }
        // Optimistic update
        const prev = members;
        setMembers(members.map((m) => m.uid === uid ? { ...m, role: newRole } : m));
        try {
            await facilityApi.changeRole(facilityId, uid, { role: newRole });
            notify.success('Role updated');
        } catch (err) {
            setMembers(prev);
            notify.error(err?.response?.data?.detail || 'Failed to update role');
        }
    };

    const onRemoveMember = async (uid) => {
        const member = members.find((m) => m.uid === uid);
        const name = member?.name || uid;
        if (!window.confirm(`Remove ${name} from this facility?`)) return;
        try {
            await facilityApi.removeMember(facilityId, uid);
            notify.success('Member removed');
            reload();
        } catch (err) {
            notify.error(err?.response?.data?.detail || 'Failed to remove member');
        }
    };

    const onSubmitInvite = async ({ role, email }) => {
        try {
            const result = await facilityApi.createInvite(facilityId, { role, email });
            if (email && result?.email_sent) {
                notify.success(`Invite sent to ${email}`);
            } else if (email && result && result.email_sent === false) {
                notify.warning('Invite created, but email delivery failed. Copy the link manually.');
            } else {
                notify.success('Invite link created');
            }
            setComposerOpen(false);
            reload();
        } catch (err) {
            notify.error(err?.response?.data?.detail || 'Failed to create invite');
        }
    };

    const onCopyInviteLink = async (code) => {
        const url = `${window.location.origin}/register?code=${code}`;
        try {
            await navigator.clipboard.writeText(url);
            notify.success('Invite link copied');
        } catch {
            notify.error('Failed to copy link');
        }
    };

    const onRevokeInvite = async (code) => {
        if (!window.confirm('Revoke this invite? The code will stop working.')) return;
        try {
            await facilityApi.revokeInvite(facilityId, code);
            notify.success('Invite revoked');
            reload();
        } catch (err) {
            notify.error(err?.response?.data?.detail || 'Failed to revoke invite');
        }
    };

    const facilityName = facility?.name || facilityId;

    return (
        <div className="facility-team-page">
            <header className="team-page-header">
                <div>
                    <div className="team-page-eyebrow">{facilityName}</div>
                    <h1 className="team-page-title">Team</h1>
                    <p className="team-page-subtitle">Manage who can access this facility and what they can do.</p>
                </div>
                <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => setComposerOpen((v) => !v)}
                >
                    + Invite member
                </button>
            </header>

            <section className="team-section">
                <div className="team-section-head">
                    <div className="team-section-head-left">
                        <h2 className="team-section-title">Members</h2>
                        <span className="team-section-count">{members.length}</span>
                    </div>
                </div>
                <div className="team-section-body">
                    {loading ? (
                        <div className="team-loading">Loading…</div>
                    ) : members.length === 0 ? (
                        <div className="team-empty">
                            <div className="team-empty-icon">
                                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <circle cx="12" cy="8" r="4" />
                                    <path d="M4 21c0-4.4 3.6-8 8-8s8 3.6 8 8" />
                                </svg>
                            </div>
                            <p className="team-empty-title">No team members yet</p>
                            <p className="team-empty-sub">Invite someone to get started.</p>
                        </div>
                    ) : (
                        members.map((m) => (
                            <TeamMemberRow
                                key={m.uid}
                                member={m}
                                currentUid={user?.uid}
                                onChangeRole={onChangeRole}
                                onRemove={onRemoveMember}
                            />
                        ))
                    )}
                </div>
            </section>

            <section className="team-section">
                <div className="team-section-head">
                    <div className="team-section-head-left">
                        <h2 className="team-section-title">Pending invites</h2>
                        <span className="team-section-count">{invites.length}</span>
                    </div>
                </div>
                {composerOpen && (
                    <InviteComposer
                        onSubmit={onSubmitInvite}
                        onCancel={() => setComposerOpen(false)}
                    />
                )}
                <div className="team-section-body">
                    {loading ? (
                        <div className="team-loading">Loading…</div>
                    ) : invites.length === 0 ? (
                        <div className="team-empty">
                            <p className="team-empty-sub">No outstanding invites.</p>
                        </div>
                    ) : (
                        invites.map((i) => (
                            <TeamInviteRow
                                key={i.code}
                                invite={i}
                                onCopy={onCopyInviteLink}
                                onRevoke={onRevokeInvite}
                            />
                        ))
                    )}
                </div>
            </section>
        </div>
    );
}
